/**
 * Tournament detail page header (iter271 — extracted from TournamentDetail.js).
 *
 * Renders the back button, banner image/lightbox, title block (name +
 * dates + location + description), and the right-hand action cluster
 * (Edit Tournament, Add Players, Send Reminders, Repair Tools popover,
 * status badge).
 *
 * Pure presentational — all state and handlers live in TournamentDetail.js
 * and are passed in as props. This keeps the page-level component focused
 * on data fetching + tab orchestration, and lets the header be tested
 * in isolation.
 */
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
  ArrowLeft, Calendar, MapPin, Trophy, Pencil, UserPlus, Wrench, ChevronDown,
} from 'lucide-react';
import ImageLightbox from '../../shared/ImageLightbox';
import AgeCategoryRepairButton from './AgeCategoryRepairButton';
import RepairTeamStatusesButton from './RepairTeamStatusesButton';
import RepairPaymentAmountsButton from './RepairPaymentAmountsButton';
import SendPaymentRemindersButton from './SendPaymentRemindersButton';
import { statusColors } from './constants';
import { sanitizeHtml } from '../../../utils/sanitize';

export default function TournamentDetailHeader({
  tournament,
  tournamentsListPath,
  isStaffOrAdmin,
  unpaidCount,
  onEditTournament,
  onAddPlayers,
  onRegistrationsRefresh,
  onTeamsRefresh,
}) {
  const navigate = useNavigate();
  const tournamentId = tournament.id;
  return (
    <div className="flex flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between gap-4">
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(tournamentsListPath)}
          className="mt-1 flex-shrink-0"
          data-testid="tournament-detail-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />Back
        </Button>
        {tournament.image_path ? (
          <ImageLightbox
            src={`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`}
            alt={tournament.name}
            triggerClassName="flex-shrink-0"
            className="h-20 w-20 sm:h-24 sm:w-24"
            testId="admin-tournament-image"
          />
        ) : (
          <div className="flex-shrink-0 h-20 w-20 sm:h-24 sm:w-24 bg-emerald-50 rounded-lg flex items-center justify-center border">
            <Trophy className="h-10 w-10 text-emerald-300" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1
            className="text-2xl font-bold break-words"
            data-testid="tournament-detail-name"
          >
            {tournament.name}
          </h1>
          <p className="text-slate-500 flex items-center gap-x-2 gap-y-1 flex-wrap text-sm">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Calendar className="h-4 w-4" />{tournament.start_date} to {tournament.end_date}
            </span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <MapPin className="h-4 w-4" />
              {tournament.location_type === 'local' ? 'Local' : 'Out of State'}
              {tournament.location && <span className="text-slate-600">· {tournament.location}</span>}
            </span>
          </p>
          {tournament.details && (
            <div
              className="prose prose-sm max-w-none mt-3 text-slate-700 line-clamp-3"
              data-testid="tournament-detail-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(tournament.details) }}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap 2xl:justify-end">
        {isStaffOrAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={onEditTournament}
            className="text-blue-600 hover:text-blue-700"
            data-testid="edit-tournament-btn"
          >
            <Pencil className="h-4 w-4 mr-1" />Edit Tournament
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onAddPlayers}
          className="text-emerald-600 hover:text-emerald-700"
          data-testid="add-players-btn"
        >
          <UserPlus className="h-4 w-4 mr-1" />Add Players
        </Button>
        <SendPaymentRemindersButton
          tournamentId={tournamentId}
          unpaidCount={unpaidCount}
          onSent={onRegistrationsRefresh}
        />

        {/* Maintenance / repair tools — collapsed into a popover so the
            header doesn't compete with the title for horizontal room.
            Each button keeps its own dialog state. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="text-slate-600 hover:text-slate-800"
              data-testid="tournament-repair-tools-trigger"
            >
              <Wrench className="h-4 w-4 mr-1" />
              Repair Tools
              <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-2 space-y-1">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide px-2 pt-1 pb-1">
              Maintenance
            </p>
            <div className="flex flex-col gap-1" data-testid="tournament-repair-tools-list">
              <AgeCategoryRepairButton
                tournamentId={tournamentId}
                onRepaired={() => { onRegistrationsRefresh?.(); onTeamsRefresh?.(); }}
              />
              <RepairTeamStatusesButton
                tournamentId={tournamentId}
                onRepaired={() => { onRegistrationsRefresh?.(); onTeamsRefresh?.(); }}
              />
              <RepairPaymentAmountsButton
                tournamentId={tournamentId}
                onRepaired={onRegistrationsRefresh}
              />
            </div>
          </PopoverContent>
        </Popover>

        <Badge className={statusColors[tournament.status]}>
          {tournament.status.toUpperCase()}
        </Badge>
      </div>
    </div>
  );
}
